<template>
  <div class="admin-settings">
    <v-container fluid>
      <!-- Header -->
      <div class="d-flex align-center justify-space-between mb-6">
        <div>
          <h1 class="text-h4 text-gradient mb-2">{{ $t('settings.systemSettings') }}</h1>
          <p class="text-subtitle-1 text-medium-emphasis">
            {{ $t('settings.systemSettingsDesc') }}
          </p>
        </div>
        <v-btn
          color="success"
          size="large"
          :loading="saving"
          @click="saveAllSettings"
        >
          <v-icon start>mdi-content-save</v-icon>
          {{ $t('settings.saveAll') }}
        </v-btn>
      </div>

      <!-- Quick Links -->
      <v-row class="mb-2">
        <v-col cols="12" sm="6" md="4">
          <v-card
            class="quick-link-card"
            color="primary"
            variant="tonal"
            hover
            to="/admin/mail"
          >
            <v-card-text class="d-flex align-center" style="gap: 16px">
              <v-icon size="36" color="primary">mdi-email-multiple-outline</v-icon>
              <div class="flex-grow-1">
                <div class="text-subtitle-1 font-weight-bold">Webmail</div>
                <div class="text-caption text-medium-emphasis">
                  Корпоративтік пошта: оқу, жіберу, пароль
                </div>
              </div>
              <v-icon size="20">mdi-arrow-right</v-icon>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <!-- Settings Tabs -->
          <v-tabs v-model="currentTab" class="mb-6">
            <v-tab value="company">
              <v-icon start>mdi-domain</v-icon>
              {{ $t('settings.tabs.company') }}
            </v-tab>
            <v-tab value="contact">
              <v-icon start>mdi-phone</v-icon>
              {{ $t('settings.tabs.contact') }}
            </v-tab>
            <v-tab value="courses">
              <v-icon start>mdi-school</v-icon>
              {{ $t('settings.tabs.courses') }}
            </v-tab>
            <v-tab value="content">
              <v-icon start>mdi-text-box</v-icon>
              {{ $t('settings.tabs.content') }}
            </v-tab>
            <v-tab value="system">
              <v-icon start>mdi-cog</v-icon>
              {{ $t('settings.tabs.system') }}
            </v-tab>
            <v-tab value="notifications">
              <v-icon start>mdi-bell</v-icon>
              {{ $t('settings.tabs.notifications') }}
            </v-tab>
          </v-tabs>

          <!-- Tab Content -->
          <v-window v-model="currentTab">
            <!-- Company Information Tab -->
            <v-window-item value="company">
              <v-card elevation="2">
                <v-card-title class="d-flex align-center">
                  <v-icon icon="mdi-domain" class="me-2" />
                  {{ $t('settings.companyInfo') }}
                </v-card-title>
                <v-divider />
                <v-card-text class="pa-6">
                  <v-row>
                    <!-- Company Name -->
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="settings.company_name.kz"
                        :label="$t('settings.companyName') + ' (KZ)'"
                        variant="outlined"
                        prepend-inner-icon="mdi-domain"
                        class="mb-4"
                      />
                      <v-text-field
                        v-model="settings.company_name.ru"
                        :label="$t('settings.companyName') + ' (RU)'"
                        variant="outlined"
                        prepend-inner-icon="mdi-domain"
                        class="mb-4"
                      />
                      <v-text-field
                        v-model="settings.company_name.en"
                        :label="$t('settings.companyName') + ' (EN)'"
                        variant="outlined"
                        prepend-inner-icon="mdi-domain"
                      />
                    </v-col>

                    <!-- Company Description -->
                    <v-col cols="12" md="6">
                      <v-textarea
                        v-model="settings.company_description.kz"
                        :label="$t('settings.companyDescription') + ' (KZ)'"
                        variant="outlined"
                        prepend-inner-icon="mdi-text"
                        rows="3"
                        class="mb-4"
                      />
                      <v-textarea
                        v-model="settings.company_description.ru"
                        :label="$t('settings.companyDescription') + ' (RU)'"
                        variant="outlined"
                        prepend-inner-icon="mdi-text"
                        rows="3"
                        class="mb-4"
                      />
                      <v-textarea
                        v-model="settings.company_description.en"
                        :label="$t('settings.companyDescription') + ' (EN)'"
                        variant="outlined"
                        prepend-inner-icon="mdi-text"
                        rows="3"
                      />
                    </v-col>
                  </v-row>

                  <!-- Avtomektep link -->
                  <v-divider class="my-6" />
                  <div class="d-flex align-center mb-3">
                    <v-icon icon="mdi-car-multiple" color="purple" class="me-2" />
                    <span class="text-subtitle-1 font-weight-medium">{{ $t('settings.avtomektepUrl') }}</span>
                  </div>
                  <v-text-field
                    v-model="settings.avtomektep_url.kz"
                    :label="$t('settings.avtomektepUrlLabel')"
                    variant="outlined"
                    prepend-inner-icon="mdi-link"
                    placeholder="https://avtomektep.kz/integration/find"
                    hint="Автомектеп батырмасы ашатын сілтеме"
                    persistent-hint
                    @blur="syncAvtomektepUrl"
                  />

                </v-card-text>
              </v-card>
            </v-window-item>

            <!-- Contact Information Tab -->
            <v-window-item value="contact">
              <v-card elevation="2">
                <v-card-title class="d-flex align-center">
                  <v-icon icon="mdi-phone" class="me-2" />
                  {{ $t('settings.contactInfo') }}
                </v-card-title>
                <v-divider />
                <v-card-text class="pa-6">
                  <v-row>
                    <!-- Contact Phone -->
                    <v-col cols="12" md="6">
                      <PhoneInput
                        v-model="settings.contact_phone.kz"
                        :label="$t('settings.contactPhone') + ' (KZ)'"
                        class="mb-4"
                      />
                      <PhoneInput
                        v-model="settings.contact_phone.ru"
                        :label="$t('settings.contactPhone') + ' (RU)'"
                        class="mb-4"
                      />
                      <PhoneInput
                        v-model="settings.contact_phone.en"
                        :label="$t('settings.contactPhone') + ' (EN)'"
                      />
                    </v-col>

                    <!-- Email -->
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="settings.contact_email.kz"
                        :label="$t('settings.contactEmail') + ' (KZ)'"
                        variant="outlined"
                        prepend-inner-icon="mdi-email"
                        type="email"
                        placeholder="info@korkem-auto.kz"
                        class="mb-4"
                      />
                      <v-text-field
                        v-model="settings.contact_email.ru"
                        :label="$t('settings.contactEmail') + ' (RU)'"
                        variant="outlined"
                        prepend-inner-icon="mdi-email"
                        type="email"
                        placeholder="info@korkem-auto.kz"
                        class="mb-4"
                      />
                      <v-text-field
                        v-model="settings.contact_email.en"
                        :label="$t('settings.contactEmail') + ' (EN)'"
                        variant="outlined"
                        prepend-inner-icon="mdi-email"
                        type="email"
                        placeholder="info@korkem-auto.kz"
                      />
                    </v-col>

                    <!-- Address -->
                    <v-col cols="12">
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-textarea
                            v-model="settings.contact_address.kz"
                            :label="$t('settings.contactAddress') + ' (KZ)'"
                            variant="outlined"
                            prepend-inner-icon="mdi-map-marker"
                            rows="3"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-textarea
                            v-model="settings.contact_address.ru"
                            :label="$t('settings.contactAddress') + ' (RU)'"
                            variant="outlined"
                            prepend-inner-icon="mdi-map-marker"
                            rows="3"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-textarea
                            v-model="settings.contact_address.en"
                            :label="$t('settings.contactAddress') + ' (EN)'"
                            variant="outlined"
                            prepend-inner-icon="mdi-map-marker"
                            rows="3"
                          />
                        </v-col>
                      </v-row>
                    </v-col>

                    <!-- Social Media -->
                    <v-col cols="12">
                      <h4 class="text-h6 mb-4">{{ $t('settings.socialMedia') }}</h4>
                      <v-row>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model="settings.social_instagram"
                            label="Instagram"
                            variant="outlined"
                            prepend-inner-icon="mdi-instagram"
                            placeholder="@korkem_auto"
                          />
                        </v-col>
                        <v-col cols="12" md="3">
                          <PhoneInput
                            v-model="settings.social_whatsapp"
                            label="WhatsApp"
                          >
                            <template #prepend-inner>
                              <v-icon color="green">mdi-whatsapp</v-icon>
                            </template>
                          </PhoneInput>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model="settings.social_telegram"
                            label="Telegram"
                            variant="outlined"
                            prepend-inner-icon="mdi-telegram"
                            placeholder="@korkem_auto"
                          />
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model="settings.social_facebook"
                            label="Facebook"
                            variant="outlined"
                            prepend-inner-icon="mdi-facebook"
                            placeholder="korkem.auto"
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>

                  <v-divider class="my-6" />

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card color="primary" variant="tonal" class="contact-preview-card">
                        <v-card-title class="d-flex justify-space-between align-center">
                          <div class="d-flex align-center">
                            <v-icon class="me-2">mdi-eye</v-icon>
                            {{ $t('settings.preview') }}
                          </div>
                          <v-btn-toggle
                            v-model="contactPreviewLocale"
                            density="compact"
                            divided
                            mandatory
                          >
                            <v-btn value="kz">KZ</v-btn>
                            <v-btn value="ru">RU</v-btn>
                            <v-btn value="en">EN</v-btn>
                          </v-btn-toggle>
                        </v-card-title>

                        <v-card-text>
                          <v-alert
                            type="info"
                            variant="tonal"
                            class="mb-4"
                            density="compact"
                          >
                            {{ $t('settings.currentContactInfo') }}
                          </v-alert>

                          <v-list class="bg-transparent" density="comfortable">
                            <v-list-item>
                              <template #prepend>
                                <v-icon color="primary">mdi-phone</v-icon>
                              </template>
                              <v-list-item-title>{{ contactPreview.phone }}</v-list-item-title>
                            </v-list-item>

                            <v-list-item>
                              <template #prepend>
                                <v-icon color="primary">mdi-email</v-icon>
                              </template>
                              <v-list-item-title>{{ contactPreview.email }}</v-list-item-title>
                            </v-list-item>

                            <v-list-item>
                              <template #prepend>
                                <v-icon color="primary">mdi-map-marker</v-icon>
                              </template>
                              <v-list-item-title>{{ contactPreview.address }}</v-list-item-title>
                            </v-list-item>

                            <v-list-item>
                              <template #prepend>
                                <v-icon color="primary">mdi-clock-outline</v-icon>
                              </template>
                              <v-list-item-title>{{ contactPreview.worktime }}</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-window-item>

            <!-- Courses Settings Tab -->
            <v-window-item value="courses">
              <v-card elevation="2">
                <v-card-title class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center">
                    <v-icon icon="mdi-school" class="me-2" />
                    {{ $t('settings.courses.title') }}
                  </div>
                  <v-btn color="primary" prepend-icon="mdi-plus" @click="openCourseDialog()">
                    {{ $t('settings.courses.addCourse') }}
                  </v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="pa-6">
                  <v-alert type="info" variant="tonal" class="mb-4" closable>
                    <v-icon start>mdi-information</v-icon>
                    {{ $t('settings.courses.info') }}
                  </v-alert>
                  
                  <div v-if="loadingCourses" class="d-flex justify-center pa-4">
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  </div>

                  <v-row v-else>
                    <v-col v-for="course in courses" :key="course.id" cols="12" md="4">
                      <v-card outlined class="h-100 d-flex flex-column">
                        <v-card-title class="d-flex align-center justify-space-between">
                          <div class="d-flex align-center">
                            <v-icon :color="course.type === 'FORMAT' ? 'primary' : 'warning'" class="me-2">
                              {{ course.type === 'FORMAT' ? 'mdi-laptop' : 'mdi-star' }}
                            </v-icon>
                            {{ course.title_kz }}
                          </div>
                          <v-chip :color="course.is_active ? 'success' : 'grey'" size="small">
                            {{ course.is_active ? $t('settings.courses.active') : $t('settings.courses.hidden') }}
                          </v-chip>
                        </v-card-title>
                        <v-card-subtitle>
                          {{ course.type === 'FORMAT' ? $t('settings.courses.format') : $t('settings.courses.tariff') }} | {{ $t('settings.courses.order') }}: {{ course.order }}
                        </v-card-subtitle>
                        <v-card-text class="flex-grow-1">
                          <div class="d-flex align-baseline mb-2">
                            <span class="text-h5 font-weight-bold text-primary me-2">{{ course.price }} ₸</span>
                            <span v-if="course.old_price" class="text-decoration-line-through text-medium-emphasis text-caption">
                              {{ course.old_price }} ₸
                            </span>
                          </div>
                          
                          <v-list density="compact" class="bg-transparent">
                            <v-list-item v-for="(feature, idx) in course.features_kz.slice(0, 3)" :key="idx" density="compact">
                              <template #prepend>
                                <v-icon size="small" color="success">mdi-check</v-icon>
                              </template>
                              <v-list-item-title class="text-caption">{{ feature }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="course.features_kz.length > 3" density="compact">
                              <v-list-item-title class="text-caption text-medium-emphasis">
                                {{ $t('settings.courses.moreFeatures', { count: course.features_kz.length - 3 }) }}
                              </v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-card-text>
                        <v-divider></v-divider>
                        <v-card-actions>
                          <v-btn variant="text" color="primary" @click="openCourseDialog(course)">
                            {{ $t('settings.courses.edit') }}
                          </v-btn>
                          <v-spacer></v-spacer>
                          <v-btn 
                            variant="text" 
                            :color="course.is_active ? 'warning' : 'success'" 
                            @click="toggleCourseActive(course)"
                            icon
                          >
                            <v-icon>{{ course.is_active ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                          </v-btn>
                          <v-btn variant="text" color="error" icon @click="confirmDeleteCourse(course)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-window-item>

            <!-- Content and Texts Tab -->
            <v-window-item value="content">
              <v-card elevation="2">
                <v-card-title class="d-flex align-center">
                  <v-icon icon="mdi-text-box" class="me-2" />
                  {{ $t('settings.content.title') }}
                </v-card-title>
                <v-divider />
                <v-card-text class="pa-6">
                  <v-row>
                    <!-- About Us Text -->
                    <v-col cols="12">
                      <h4 class="text-h6 mb-4">{{ $t('settings.content.aboutUs') }}</h4>
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-textarea
                            v-model="settings.about_us.kz"
                            :label="$t('settings.content.aboutUsLabel') + ' (KZ)'"
                            variant="outlined"
                            rows="6"
                            :hint="$t('settings.content.htmlHint')"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-textarea
                            v-model="settings.about_us.ru"
                            :label="$t('settings.content.aboutUsLabel') + ' (RU)'"
                            variant="outlined"
                            rows="6"
                            :hint="$t('settings.content.htmlHint')"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-textarea
                            v-model="settings.about_us.en"
                            :label="$t('settings.content.aboutUsLabel') + ' (EN)'"
                            variant="outlined"
                            rows="6"
                            :hint="$t('settings.content.htmlHint')"
                          />
                        </v-col>
                      </v-row>
                    </v-col>

                    <!-- Welcome Message -->
                    <v-col cols="12">
                      <h4 class="text-h6 mb-4">{{ $t('settings.content.welcomeMsg') }}</h4>
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-textarea
                            v-model="settings.welcome_message.kz"
                            :label="$t('settings.content.welcomeMsgLabel') + ' (KZ)'"
                            variant="outlined"
                            rows="4"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-textarea
                            v-model="settings.welcome_message.ru"
                            :label="$t('settings.content.welcomeMsgLabel') + ' (RU)'"
                            variant="outlined"
                            rows="4"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-textarea
                            v-model="settings.welcome_message.en"
                            :label="$t('settings.content.welcomeMsgLabel') + ' (EN)'"
                            variant="outlined"
                            rows="4"
                          />
                        </v-col>
                      </v-row>
                    </v-col>

                    <!-- Terms and Conditions -->
                    <v-col cols="12">
                      <h4 class="text-h6 mb-4">{{ $t('settings.content.terms') }}</h4>
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-textarea
                            v-model="settings.terms_conditions.kz"
                            :label="$t('settings.content.termsLabel') + ' (KZ)'"
                            variant="outlined"
                            rows="8"
                            :hint="$t('settings.content.htmlHint')"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-textarea
                            v-model="settings.terms_conditions.ru"
                            :label="$t('settings.content.termsLabel') + ' (RU)'"
                            variant="outlined"
                            rows="8"
                            :hint="$t('settings.content.htmlHint')"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-textarea
                            v-model="settings.terms_conditions.en"
                            :label="$t('settings.content.termsLabel') + ' (EN)'"
                            variant="outlined"
                            rows="8"
                            :hint="$t('settings.content.htmlHint')"
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-window-item>

            <!-- System Settings Tab -->
            <v-window-item value="system">
              <v-card elevation="2">
                <v-card-title class="d-flex align-center">
                  <v-icon icon="mdi-cog" class="me-2" />
                  {{ $t('settings.system.title') }}
                </v-card-title>
                <v-divider />
                <v-card-text class="pa-6">
                  <v-row>
                    <!-- Registration Settings -->
                    <v-col cols="12" md="6">
                      <h4 class="text-h6 mb-4">{{ $t('settings.system.registration') }}</h4>
                      
                      <v-switch
                        v-model="settings.registration_enabled"
                        :label="$t('settings.system.regEnabled')"
                        color="primary"
                        inset
                        class="mb-4"
                      />

                      <v-switch
                        v-model="settings.email_verification_required"
                        :label="$t('settings.system.emailVerify')"
                        color="primary"
                        inset
                        class="mb-4"
                        :disabled="!settings.registration_enabled"
                      />

                      <v-switch
                        v-model="settings.admin_approval_required"
                        :label="$t('settings.system.adminApproval')"
                        color="primary"
                        inset
                        class="mb-4"
                        :disabled="!settings.registration_enabled"
                      />

                      <v-text-field
                        v-model.number="settings.max_students_per_instructor"
                        :label="$t('settings.system.maxStudents')"
                        variant="outlined"
                        type="number"
                        min="1"
                        max="100"
                        prepend-inner-icon="mdi-account-group"
                      />
                    </v-col>

                    <!-- Test Settings -->
                    <v-col cols="12" md="6">
                      <h4 class="text-h6 mb-4">{{ $t('settings.system.testSettings') }}</h4>
                      
                      <v-text-field
                        v-model.number="settings.exam_duration_minutes"
                        :label="$t('settings.system.examDuration')"
                        variant="outlined"
                        type="number"
                        min="10"
                        max="120"
                        prepend-inner-icon="mdi-clock"
                        class="mb-4"
                      />

                      <v-text-field
                        v-model.number="settings.exam_questions_count"
                        :label="$t('settings.system.examQuestions')"
                        variant="outlined"
                        type="number"
                        min="10"
                        max="100"
                        prepend-inner-icon="mdi-help-circle"
                        class="mb-4"
                      />

                      <v-text-field
                        v-model.number="settings.passing_score_percentage"
                        :label="$t('settings.system.passingScore')"
                        variant="outlined"
                        type="number"
                        min="50"
                        max="100"
                        prepend-inner-icon="mdi-percent"
                        class="mb-4"
                      />

                      <v-switch
                        v-model="settings.allow_retake_exam"
                        :label="$t('settings.system.allowRetake')"
                        color="primary"
                        inset
                      />
                    </v-col>

                    <!-- File Upload Settings -->
                    <v-col cols="12">
                      <h4 class="text-h6 mb-4">{{ $t('settings.system.fileSettings') }}</h4>
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model.number="settings.max_file_size_mb"
                            :label="$t('settings.system.maxFileSize')"
                            variant="outlined"
                            type="number"
                            min="1"
                            max="100"
                            prepend-inner-icon="mdi-file-upload"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="settings.allowed_file_types"
                            :label="$t('settings.system.allowedTypes')"
                            variant="outlined"
                            prepend-inner-icon="mdi-file-multiple"
                            placeholder="jpg,png,pdf,doc,docx"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model.number="settings.max_files_per_user"
                            :label="$t('settings.system.maxFiles')"
                            variant="outlined"
                            type="number"
                            min="1"
                            max="50"
                            prepend-inner-icon="mdi-counter"
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-window-item>

            <!-- Notifications Tab -->
            <v-window-item value="notifications">
              <v-card elevation="2">
                <v-card-title class="d-flex align-center">
                  <v-icon icon="mdi-bell" class="me-2" />
                  {{ $t('settings.notifications.title') }}
                </v-card-title>
                <v-divider />
                <v-card-text class="pa-6">
                  <v-row>
                    <!-- Email Notifications -->
                    <v-col cols="12" md="6">
                      <h4 class="text-h6 mb-4">{{ $t('settings.notifications.emailTitle') }}</h4>
                      
                      <v-switch
                        v-model="settings.email_notifications_enabled"
                        :label="$t('settings.notifications.emailEnabled')"
                        color="primary"
                        inset
                        class="mb-4"
                      />

                      <v-text-field
                        v-model="settings.smtp_host"
                        :label="$t('settings.notifications.smtpHost')"
                        variant="outlined"
                        prepend-inner-icon="mdi-server"
                        :disabled="!settings.email_notifications_enabled"
                        class="mb-4"
                      />

                      <v-text-field
                        v-model.number="settings.smtp_port"
                        :label="$t('settings.notifications.smtpPort')"
                        variant="outlined"
                        type="number"
                        prepend-inner-icon="mdi-numeric"
                        :disabled="!settings.email_notifications_enabled"
                        class="mb-4"
                      />

                      <v-text-field
                        v-model="settings.smtp_username"
                        :label="$t('settings.notifications.smtpUser')"
                        variant="outlined"
                        prepend-inner-icon="mdi-account"
                        :disabled="!settings.email_notifications_enabled"
                        class="mb-4"
                      />

                      <v-text-field
                        v-model="settings.smtp_password"
                        :label="$t('settings.notifications.smtpPass')"
                        variant="outlined"
                        type="password"
                        prepend-inner-icon="mdi-lock"
                        :disabled="!settings.email_notifications_enabled"
                      />
                    </v-col>

                    <!-- SMS Notifications -->
                    <v-col cols="12" md="6">
                      <h4 class="text-h6 mb-4">{{ $t('settings.notifications.smsTitle') }}</h4>
                      
                      <v-switch
                        v-model="settings.sms_notifications_enabled"
                        :label="$t('settings.notifications.smsEnabled')"
                        color="primary"
                        inset
                        class="mb-4"
                      />

                      <v-text-field
                        v-model="settings.sms_provider"
                        :label="$t('settings.notifications.smsProvider')"
                        variant="outlined"
                        prepend-inner-icon="mdi-cellphone"
                        :disabled="!settings.sms_notifications_enabled"
                        class="mb-4"
                      />

                      <v-text-field
                        v-model="settings.sms_api_key"
                        :label="$t('settings.notifications.smsApiKey')"
                        variant="outlined"
                        prepend-inner-icon="mdi-key"
                        :disabled="!settings.sms_notifications_enabled"
                        class="mb-4"
                      />

                      <v-text-field
                        v-model="settings.sms_sender_name"
                        :label="$t('settings.notifications.senderName')"
                        variant="outlined"
                        prepend-inner-icon="mdi-account-circle"
                        :disabled="!settings.sms_notifications_enabled"
                        placeholder="KORKEM-AUTO"
                      />
                    </v-col>

                    <!-- Notification Templates -->
                    <v-col cols="12">
                      <h4 class="text-h6 mb-4">{{ $t('settings.notifications.templates') }}</h4>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-textarea
                            v-model="settings.welcome_email_template"
                            :label="$t('settings.notifications.welcomeTemplate')"
                            variant="outlined"
                            rows="4"
                            :hint="$t('settings.notifications.welcomeHint')"
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea
                            v-model="settings.password_reset_sms_template"
                            :label="$t('settings.notifications.resetTemplate')"
                            variant="outlined"
                            rows="4"
                            :hint="$t('settings.notifications.resetHint')"
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-window-item>
          </v-window>

          <!-- Save All Button (Fixed) -->
          <div class="mt-6 text-center">
            <v-btn
              color="success"
              size="x-large"
              :loading="saving"
              @click="saveAllSettings"
            >
              <v-icon start>mdi-content-save-all</v-icon>
              {{ $t('settings.saveAll') }}
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- Success/Error Messages -->
      <v-snackbar
        v-model="showSuccessMessage"
        color="success"
        timeout="3000"
      >
        <v-icon start>mdi-check</v-icon>
        {{ $t('settings.messages.success') }}
      </v-snackbar>

      <v-snackbar
        v-model="showErrorMessage"
        color="error"
        timeout="5000"
      >
        <v-icon start>mdi-alert</v-icon>
        {{ $t('settings.messages.error', { msg: errorMessage }) }}
      </v-snackbar>

      <!-- Course Edit/Create Dialog -->
      <v-dialog v-model="courseDialog" max-width="900px" persistent>
        <v-card>
          <v-card-title>
            <span class="text-h5">{{ editedCourse.id ? $t('settings.courses.editTitle') : $t('settings.courses.addTitle') }}</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <!-- Basic Info -->
                <v-col cols="12" md="6">
                  <v-select
                    v-model="editedCourse.type"
                    :items="courseTypes"
                    :label="$t('settings.courses.type')"
                    variant="outlined"
                    required
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="editedCourse.order"
                    :label="$t('settings.courses.order')"
                    type="number"
                    variant="outlined"
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="editedCourse.price"
                    :label="$t('settings.courses.price')"
                    type="number"
                    variant="outlined"
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="editedCourse.old_price"
                    :label="$t('settings.courses.oldPrice')"
                    type="number"
                    variant="outlined"
                    :hint="$t('settings.courses.oldPriceHint')"
                    persistent-hint
                  ></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-divider class="my-4"></v-divider>
                  <div class="text-subtitle-1 mb-2">{{ $t('settings.courses.nameAndDesc') }}</div>
                  <v-tabs v-model="editedCourse.activeLangTab" density="compact" class="mb-4">
                    <v-tab value="kz">Қазақша</v-tab>
                    <v-tab value="ru">Русский</v-tab>
                    <v-tab value="en">English</v-tab>
                  </v-tabs>
                  
                  <v-window v-model="editedCourse.activeLangTab">
                    <v-window-item value="kz">
                      <v-text-field v-model="editedCourse.title_kz" :label="$t('settings.courses.titleLabel') + ' (KZ)'" variant="outlined"></v-text-field>
                      <v-textarea v-model="editedCourse.description_kz" :label="$t('settings.courses.descLabel') + ' (KZ)'" variant="outlined" rows="3"></v-textarea>
                      
                      <div class="d-flex align-center mt-2">
                        <v-text-field 
                          v-model="featureInputKz" 
                          :label="$t('settings.courses.addFeature') + ' (KZ)'" 
                          variant="outlined" 
                          density="compact"
                          hide-details
                          @keyup.enter="addFeature('kz')"
                        >
                          <template #append>
                            <v-btn icon="mdi-plus" size="small" color="primary" @click="addFeature('kz')"></v-btn>
                          </template>
                        </v-text-field>
                      </div>
                      <v-chip-group class="mt-2" column>
                        <v-chip v-for="(feat, i) in editedCourse.features_kz" :key="i" closable @click:close="removeFeature('kz', i)">
                          {{ feat }}
                        </v-chip>
                      </v-chip-group>
                    </v-window-item>

                    <v-window-item value="ru">
                      <v-text-field v-model="editedCourse.title_ru" :label="$t('settings.courses.titleLabel') + ' (RU)'" variant="outlined"></v-text-field>
                      <v-textarea v-model="editedCourse.description_ru" :label="$t('settings.courses.descLabel') + ' (RU)'" variant="outlined" rows="3"></v-textarea>
                      
                      <div class="d-flex align-center mt-2">
                        <v-text-field 
                          v-model="featureInputRu" 
                          :label="$t('settings.courses.addFeature') + ' (RU)'" 
                          variant="outlined" 
                          density="compact"
                          hide-details
                          @keyup.enter="addFeature('ru')"
                        >
                          <template #append>
                            <v-btn icon="mdi-plus" size="small" color="primary" @click="addFeature('ru')"></v-btn>
                          </template>
                        </v-text-field>
                      </div>
                      <v-chip-group class="mt-2" column>
                        <v-chip v-for="(feat, i) in editedCourse.features_ru" :key="i" closable @click:close="removeFeature('ru', i)">
                          {{ feat }}
                        </v-chip>
                      </v-chip-group>
                    </v-window-item>

                    <v-window-item value="en">
                      <v-text-field v-model="editedCourse.title_en" :label="$t('settings.courses.titleLabel') + ' (EN)'" variant="outlined"></v-text-field>
                      <v-textarea v-model="editedCourse.description_en" :label="$t('settings.courses.descLabel') + ' (EN)'" variant="outlined" rows="3"></v-textarea>
                      
                      <div class="d-flex align-center mt-2">
                        <v-text-field 
                          v-model="featureInputEn" 
                          :label="$t('settings.courses.addFeature') + ' (EN)'" 
                          variant="outlined" 
                          density="compact"
                          hide-details
                          @keyup.enter="addFeature('en')"
                        >
                          <template #append>
                            <v-btn icon="mdi-plus" size="small" color="primary" @click="addFeature('en')"></v-btn>
                          </template>
                        </v-text-field>
                      </div>
                      <v-chip-group class="mt-2" column>
                        <v-chip v-for="(feat, i) in editedCourse.features_en" :key="i" closable @click:close="removeFeature('en', i)">
                          {{ feat }}
                        </v-chip>
                      </v-chip-group>
                    </v-window-item>
                  </v-window>
                </v-col>

                <v-col cols="12">
                  <v-switch
                    v-model="editedCourse.is_active"
                    :label="$t('settings.courses.isActive')"
                    color="success"
                    inset
                  ></v-switch>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue-darken-1" variant="text" @click="closeCourseDialog">
              {{ $t('settings.courses.cancel') }}
            </v-btn>
            <v-btn color="blue-darken-1" variant="text" @click="saveCourse" :loading="savingCourse">
              {{ $t('settings.courses.save') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h5">{{ $t('settings.courses.deleteTitle') }}</v-card-title>
          <v-card-text>
            {{ $t('settings.courses.deleteConfirm') }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue-darken-1" variant="text" @click="deleteDialog = false">{{ $t('settings.courses.cancel') }}</v-btn>
            <v-btn color="error" variant="text" @click="deleteCourse">{{ $t('settings.courses.delete') }}</v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import api from '@/utils/api'
import PhoneInput from '@/components/ui/PhoneInput.vue'
import { usePublicSettings } from '@/composables/usePublicSettings'

const { t } = useI18n()

// Get refreshSettings function to clear cache after save
const { refreshSettings } = usePublicSettings()

// Router for tab persistence across refresh
const route = useRoute()
const router = useRouter()

// Allowed tabs for validation
const allowedTabs = ['company', 'contact', 'courses', 'content', 'system', 'notifications']

// Reactive state
const currentTab = ref('company')
const saving = ref(false)
const loading = ref(false)
const showSuccessMessage = ref(false)
const showErrorMessage = ref(false)
const errorMessage = ref('')

// Settings object with default multilingual structure
const settings = reactive({
  // Company Information
  company_name: {
    kz: '',
    ru: '',
    en: ''
  },
  company_description: {
    kz: '',
    ru: '',
    en: ''
  },

  // About Features
  about_feature1: {
    kz: '',
    ru: '',
    en: ''
  },
  about_feature2: {
    kz: '',
    ru: '',
    en: ''
  },
  about_feature3: {
    kz: '',
    ru: '',
    en: ''
  },
  about_feature4: {
    kz: '',
    ru: '',
    en: ''
  },

  // Contact Information
  contact_phone: {
    kz: '',
    ru: '',
    en: ''
  },
  contact_email: {
    kz: '',
    ru: '',
    en: ''
  },
  contact_address: {
    kz: '',
    ru: '',
    en: ''
  },
  contact_hours: {
    kz: '',
    ru: '',
    en: ''
  },

  // Social Media
  social_instagram: {
    kz: '',
    ru: '',
    en: ''
  },
  social_facebook: {
    kz: '',
    ru: '',
    en: ''
  },
  social_youtube: {
    kz: '',
    ru: '',
    en: ''
  },
  social_whatsapp: {
    kz: '',
    ru: '',
    en: ''
  },

  // External links
  avtomektep_url: {
    kz: '',
    ru: '',
    en: ''
  },

  // Button Texts
  button_select: {
    kz: '',
    ru: '',
    en: ''
  },
  button_popular: {
    kz: '',
    ru: '',
    en: ''
  },
  button_profile: {
    kz: '',
    ru: '',
    en: ''
  },
  button_instructors: {
    kz: '',
    ru: '',
    en: ''
  },

  // Online Course
  online_course_title: {
    kz: '',
    ru: '',
    en: ''
  },
  online_course_price: {
    kz: '',
    ru: '',
    en: ''
  },
  online_course_old_price: {
    kz: '',
    ru: '',
    en: ''
  },
  online_feature1: {
    kz: '',
    ru: '',
    en: ''
  },
  online_feature2: {
    kz: '',
    ru: '',
    en: ''
  },
  online_feature3: {
    kz: '',
    ru: '',
    en: ''
  },
  online_feature4: {
    kz: '',
    ru: '',
    en: ''
  },
  online_feature5: {
    kz: '',
    ru: '',
    en: ''
  },

  // Offline Course
  offline_course_title: {
    kz: '',
    ru: '',
    en: ''
  },
  offline_course_price: {
    kz: '',
    ru: '',
    en: ''
  },
  offline_course_old_price: {
    kz: '',
    ru: '',
    en: ''
  },
  offline_feature1: {
    kz: '',
    ru: '',
    en: ''
  },
  offline_feature2: {
    kz: '',
    ru: '',
    en: ''
  },
  offline_feature3: {
    kz: '',
    ru: '',
    en: ''
  },
  offline_feature4: {
    kz: '',
    ru: '',
    en: ''
  },
  offline_feature5: {
    kz: '',
    ru: '',
    en: ''
  },
  offline_feature6: {
    kz: '',
    ru: '',
    en: ''
  },

  // Combo Course
  combo_course_title: {
    kz: '',
    ru: '',
    en: ''
  },
  combo_course_price: {
    kz: '',
    ru: '',
    en: ''
  },
  combo_course_old_price: {
    kz: '',
    ru: '',
    en: ''
  },
  combo_course_bonus: {
    kz: '',
    ru: '',
    en: ''
  },
  combo_feature1: {
    kz: '',
    ru: '',
    en: ''
  },
  combo_feature2: {
    kz: '',
    ru: '',
    en: ''
  },
  combo_feature3: {
    kz: '',
    ru: '',
    en: ''
  },
  combo_feature4: {
    kz: '',
    ru: '',
    en: ''
  },
  combo_feature5: {
    kz: '',
    ru: '',
    en: ''
  },
  combo_feature6: {
    kz: '',
    ru: '',
    en: ''
  },
  contact_email: {
    kz: '',
    ru: '',
    en: ''
  },
  contact_address: {
    kz: '',
    ru: '',
    en: ''
  },

  // Social Media
  social_instagram: '',
  social_whatsapp: '',
  social_telegram: '',
  social_facebook: '',

  // Content and Texts
  about_us: {
    kz: '',
    ru: '',
    en: ''
  },
  welcome_message: {
    kz: '',
    ru: '',
    en: ''
  },
  terms_conditions: {
    kz: '',
    ru: '',
    en: ''
  },

  // System Settings
  registration_enabled: true,
  email_verification_required: false,
  admin_approval_required: true,
  max_students_per_instructor: 10,
  exam_duration_minutes: 40,
  exam_questions_count: 40,
  passing_score_percentage: 70,
  allow_retake_exam: true,

  // File Upload Settings
  max_file_size_mb: 10,
  allowed_file_types: 'jpg,jpeg,png,pdf,doc,docx',
  max_files_per_user: 10,

  // Email Notifications
  email_notifications_enabled: false,
  smtp_host: '',
  smtp_port: 587,
  smtp_username: '',
  smtp_password: '',

  // SMS Notifications
  sms_notifications_enabled: false,
  sms_provider: '',
  sms_api_key: '',
  sms_sender_name: 'KORKEM-AUTO',

  // Notification Templates
  welcome_email_template: 'Қош келдіңіз {{name}}! Сіздің тіркелгіңіз сәтті жасалды.',
  password_reset_sms_template: 'Сіздің құпия сөз қалпына келтіру кодыңыз: {{code}}'
})

const contactPreviewLocale = ref('kz')

const contactFallbacks = {
  phone: '+7 (700) 123-45-67',
  email: 'info@korkem-auto.kz',
  address: 'Алматы қ., Абай көш. 123',
  worktime: '09:00 - 20:00 (Дүйсенбі - Жексенбі)'
}

const getLocalizedSettingValue = (groupKey, locale) => {
  const entry = settings[groupKey]
  if (!entry || typeof entry !== 'object') {
    return ''
  }
  const normalizedLocale = ['kz', 'ru', 'en'].includes(locale) ? locale : 'ru'
  return entry[normalizedLocale] || entry.kz || entry.ru || entry.en || ''
}

const contactPreview = computed(() => {
  const locale = contactPreviewLocale.value
  return {
    phone: getLocalizedSettingValue('contact_phone', locale) || contactFallbacks.phone,
    email: getLocalizedSettingValue('contact_email', locale) || contactFallbacks.email,
    address: getLocalizedSettingValue('contact_address', locale) || contactFallbacks.address,
    worktime: getLocalizedSettingValue('contact_hours', locale) || contactFallbacks.worktime
  }
})

// URL fields that are language-agnostic — sync across all locales
const syncAvtomektepUrl = () => {
  const url = settings.avtomektep_url.kz || ''
  settings.avtomektep_url.ru = url
  settings.avtomektep_url.en = url
}

// Mapping functions
const mapApiKeyToSettingKey = (apiKey) => {
  // Map API keys to Settings.vue keys
  const mapping = {
    'site_name': 'company_name',
    'about.description': 'company_description',
    'avtomektep.url': 'avtomektep_url',
    'about.feature1': 'about_feature1',
    'about.feature2': 'about_feature2', 
    'about.feature3': 'about_feature3',
    'about.feature4': 'about_feature4',
    'contact.phone': 'contact_phone',
    'contact.email': 'contact_email',
    'contact.address': 'contact_address',
    'contact.worktime': 'contact_hours',
    'social.instagram': 'social_instagram',
    'social.facebook': 'social_facebook',
    'social.youtube': 'social_youtube',
    'social.whatsapp': 'social_whatsapp',
    'button.select': 'button_select',
    'button.popular': 'button_popular',
    'button.profile': 'button_profile',
    'button.instructors': 'button_instructors',
    'courses.online.title': 'online_course_title',
    'courses.online.price': 'online_course_price',
    'courses.online.oldPrice': 'online_course_old_price',
    'courses.online.feature1': 'online_feature1',
    'courses.online.feature2': 'online_feature2',
    'courses.online.feature3': 'online_feature3',
    'courses.online.feature4': 'online_feature4',
    'courses.online.feature5': 'online_feature5',
    'courses.offline.title': 'offline_course_title',
    'courses.offline.price': 'offline_course_price',
    'courses.offline.oldPrice': 'offline_course_old_price',
    'courses.offline.feature1': 'offline_feature1',
    'courses.offline.feature2': 'offline_feature2',
    'courses.offline.feature3': 'offline_feature3',
    'courses.offline.feature4': 'offline_feature4',
    'courses.offline.feature5': 'offline_feature5',
    'courses.offline.feature6': 'offline_feature6',
    'courses.combo.title': 'combo_course_title',
    'courses.combo.price': 'combo_course_price',
    'courses.combo.oldPrice': 'combo_course_old_price',
    'courses.combo.bonus': 'combo_course_bonus',
    'courses.combo.feature1': 'combo_feature1',
    'courses.combo.feature2': 'combo_feature2',
    'courses.combo.feature3': 'combo_feature3',
    'courses.combo.feature4': 'combo_feature4',
    'courses.combo.feature5': 'combo_feature5',
    'courses.combo.feature6': 'combo_feature6'
  }
  return mapping[apiKey] || apiKey.replace(/\./g, '_')
}

const mapSettingKeyToApiKey = (settingKey) => {
  // Map Settings.vue keys back to API keys
  const mapping = {
    'company_name': 'site_name',
    'company_description': 'about.description',
    'avtomektep_url': 'avtomektep.url',
    'about_feature1': 'about.feature1',
    'about_feature2': 'about.feature2',
    'about_feature3': 'about.feature3',
    'about_feature4': 'about.feature4',
    'contact_phone': 'contact.phone',
    'contact_email': 'contact.email',
    'contact_address': 'contact.address',
    'contact_hours': 'contact.worktime',
    'social_instagram': 'social.instagram',
    'social_facebook': 'social.facebook',
    'social_youtube': 'social.youtube',
    'social_whatsapp': 'social.whatsapp',
    'button_select': 'button.select',
    'button_popular': 'button.popular',
    'button_profile': 'button.profile',
    'button_instructors': 'button.instructors',
    'online_course_title': 'courses.online.title',
    'online_course_price': 'courses.online.price',
    'online_course_old_price': 'courses.online.oldPrice',
    'online_feature1': 'courses.online.feature1',
    'online_feature2': 'courses.online.feature2',
    'online_feature3': 'courses.online.feature3',
    'online_feature4': 'courses.online.feature4',
    'online_feature5': 'courses.online.feature5',
    'offline_course_title': 'courses.offline.title',
    'offline_course_price': 'courses.offline.price',
    'offline_course_old_price': 'courses.offline.oldPrice',
    'offline_feature1': 'courses.offline.feature1',
    'offline_feature2': 'courses.offline.feature2',
    'offline_feature3': 'courses.offline.feature3',
    'offline_feature4': 'courses.offline.feature4',
    'offline_feature5': 'courses.offline.feature5',
    'offline_feature6': 'courses.offline.feature6',
    'combo_course_title': 'courses.combo.title',
    'combo_course_price': 'courses.combo.price',
    'combo_course_old_price': 'courses.combo.oldPrice',
    'combo_course_bonus': 'courses.combo.bonus',
    'combo_feature1': 'courses.combo.feature1',
    'combo_feature2': 'courses.combo.feature2',
    'combo_feature3': 'courses.combo.feature3',
    'combo_feature4': 'courses.combo.feature4',
    'combo_feature5': 'courses.combo.feature5',
    'combo_feature6': 'courses.combo.feature6'
  }
  return mapping[settingKey] || settingKey.replace(/_/g, '.')
}

// Methods
const loadSettings = async () => {
  loading.value = true
  const applySettings = (data) => {
    if (!data) return
    // Explicit critical mappings for company/contact to guarantee population
    const assignMultilang = (settingKey, apiKey) => {
      const d = data[apiKey]
      if (d && settings[settingKey] && typeof settings[settingKey] === 'object') {
        settings[settingKey].kz = d.kz || settings[settingKey].kz || ''
        settings[settingKey].ru = d.ru || settings[settingKey].ru || ''
        settings[settingKey].en = d.en || settings[settingKey].en || ''
      }
    }
    // Company
    assignMultilang('company_name', 'site_name')
    assignMultilang('company_description', 'about.description')
    assignMultilang('avtomektep_url', 'avtomektep.url')
    // Contact
    assignMultilang('contact_phone', 'contact.phone')
    assignMultilang('contact_email', 'contact.email')
    assignMultilang('contact_address', 'contact.address')
    assignMultilang('contact_hours', 'contact.worktime')
    Object.keys(data).forEach(apiKey => {
      const settingData = data[apiKey]
      const settingKey = mapApiKeyToSettingKey(apiKey)
      if (settings[settingKey] !== undefined) {
        if (typeof settings[settingKey] === 'object' && settings[settingKey]?.kz !== undefined) {
          settings[settingKey].kz = settingData.kz || ''
          settings[settingKey].ru = settingData.ru || ''
          settings[settingKey].en = settingData.en || ''
        } else if (typeof settings[settingKey] === 'string') {
          settings[settingKey] = settingData.kz || settingData.ru || settingData.en || ''
        } else if (typeof settings[settingKey] === 'number') {
          const numValue = settingData.kz || settingData.ru || settingData.en || '0'
          settings[settingKey] = parseFloat(numValue) || 0
        } else if (typeof settings[settingKey] === 'boolean') {
          const boolValue = settingData.kz || settingData.ru || settingData.en || 'false'
          settings[settingKey] = boolValue === 'true' || boolValue === true
        }
      }
    })
  }

  try {
    // Try admin endpoint first
    const adminResp = await api.get('/admin/settings')
    const adminData = adminResp?.data || {}
    const adminKeysCount = Object.keys(adminData).length
    if (adminKeysCount > 0) {
      applySettings(adminData)
    } else {
      // Fallback to public settings if admin returned empty
      const publicResp = await api.get('/public/settings')
      applySettings(publicResp?.data || {})
    }
  } catch (err) {
    // Fallback to public on any error
    try {
      const publicResp = await api.get('/public/settings')
      applySettings(publicResp?.data || {})
    } catch (err2) {
      // Error:', err2)
      showError(t('settings.script.errors.load'))
    }
  } finally {
    loading.value = false
  }
}


const saveAllSettings = async () => {
  saving.value = true
  let successCount = 0
  let errorCount = 0
  
  try {
    // Convert Settings.vue format to API format
    const updates = []
    
    Object.keys(settings).forEach(settingKey => {
      const apiKey = mapSettingKeyToApiKey(settingKey)
      const value = settings[settingKey]
      
      if (typeof value === 'object' && value !== null && value.kz !== undefined) {
        // Multi-language setting
        updates.push({
          key: apiKey,
          valueKz: value.kz || '',
          valueRu: value.ru || '',
          valueEn: value.en || '',
          dataType: 'text'
        })
      } else if (typeof value === 'boolean') {
        // Boolean setting
        updates.push({
          key: apiKey,
          valueKz: value.toString(),
          valueRu: value.toString(),
          valueEn: value.toString(),
          dataType: 'boolean'
        })
      } else if (typeof value === 'number') {
        // Number setting
        updates.push({
          key: apiKey,
          valueKz: value.toString(),
          valueRu: value.toString(),
          valueEn: value.toString(),
          dataType: 'number'
        })
      } else if (typeof value === 'string' && value.trim() !== '') {
        // String setting (only if not empty)
        updates.push({
          key: apiKey,
          valueKz: value,
          valueRu: value,
          valueEn: value,
          dataType: 'text'
        })
      }
    })
    
    // Log removed
    
    // Save each setting
    for (const update of updates) {
      try {
        await api.put(`/admin/settings/${update.key}`, update)
        successCount++
        // Log removed
      } catch (error) {
        errorCount++
        // Error
      }
    }
    
    if (errorCount === 0) {
      // Log removed
      showSuccessMessage.value = true
      
      // Clear public settings cache so changes are visible immediately
      try {
        await refreshSettings()
      } catch (cacheError) {
        // Cache refresh failed but settings were saved
      }
    } else {
      showError(t('settings.script.errors.saveStats', { success: successCount, error: errorCount }))
    }
    
  } catch (error) {
    // Error
    showError(error.response?.data?.error || t('settings.script.errors.save'))
  } finally {
    saving.value = false
  }
}

const showError = (message) => {
  errorMessage.value = message
  showErrorMessage.value = true
}

// Phone formatting is now handled by PhoneInput component

// Lifecycle
onMounted(() => {
  // Restore tab from query or localStorage
  const queryTab = typeof route.query.tab === 'string' ? route.query.tab : undefined
  const savedTab = localStorage.getItem('admin-settings-active-tab') || undefined
  const initialTab = queryTab || savedTab
  if (initialTab && allowedTabs.includes(initialTab)) {
    currentTab.value = initialTab
    if (initialTab === 'courses') {
      fetchCourses()
    }
  }

  // Ensure URL reflects current tab (without adding history entries)
  const q = { ...route.query }
  if (currentTab.value !== q.tab) {
    router.replace({ query: { ...q, tab: currentTab.value } })
  }

  loadSettings()
})

// Persist tab changes to URL and localStorage
watch(currentTab, (val) => {
  if (!allowedTabs.includes(val)) return
  localStorage.setItem('admin-settings-active-tab', val)
  const q = { ...route.query }
  if (q.tab !== val) {
    router.replace({ query: { ...q, tab: val } })
  }
  
  if (val === 'courses') {
    fetchCourses()
  }
})

// React to URL changes (e.g., external navigation to specific tab)
watch(
  () => route.query.tab,
  (tab) => {
    if (typeof tab === 'string' && allowedTabs.includes(tab) && currentTab.value !== tab) {
      currentTab.value = tab
    }
  }
)

// Courses Management
const courses = ref([])
const loadingCourses = ref(false)
const courseDialog = ref(false)
const deleteDialog = ref(false)
const courseToDelete = ref(null)
const savingCourse = ref(false)

const defaultCourse = {
  id: null,
  title_ru: '',
  title_kz: '',
  title_en: '',
  description_ru: '',
  description_kz: '',
  description_en: '',
  price: 0,
  old_price: null,
  features_ru: [],
  features_kz: [],
  features_en: [],
  type: 'FORMAT', // FORMAT or TARIFF
  order: 0,
  is_active: true,
  activeLangTab: 'kz'
}

const editedCourse = ref({ ...defaultCourse })

// Feature inputs for dialog
const featureInputRu = ref('')
const featureInputKz = ref('')
const featureInputEn = ref('')

const courseTypes = computed(() => [
  { title: t('settings.script.courseTypes.format'), value: 'FORMAT' },
  { title: t('settings.script.courseTypes.tariff'), value: 'TARIFF' }
])

const fetchCourses = async () => {
  loadingCourses.value = true
  try {
    const response = await api.get('/courses/admin')
    courses.value = response.data
  } catch (error) {
    showError(t('settings.script.errors.loadCourses'))
  } finally {
    loadingCourses.value = false
  }
}

const openCourseDialog = (course = null) => {
  if (course) {
    editedCourse.value = JSON.parse(JSON.stringify(course))
    editedCourse.value.activeLangTab = 'kz'
  } else {
    editedCourse.value = JSON.parse(JSON.stringify(defaultCourse))
    // Set next order
    const maxOrder = courses.value.reduce((max, c) => Math.max(max, c.order || 0), 0)
    editedCourse.value.order = maxOrder + 1
  }
  courseDialog.value = true
}

const closeCourseDialog = () => {
  courseDialog.value = false
  setTimeout(() => {
    editedCourse.value = { ...defaultCourse }
    featureInputRu.value = ''
    featureInputKz.value = ''
    featureInputEn.value = ''
  }, 300)
}

const addFeature = (lang) => {
  if (lang === 'ru' && featureInputRu.value) {
    editedCourse.value.features_ru.push(featureInputRu.value)
    featureInputRu.value = ''
  } else if (lang === 'kz' && featureInputKz.value) {
    editedCourse.value.features_kz.push(featureInputKz.value)
    featureInputKz.value = ''
  } else if (lang === 'en' && featureInputEn.value) {
    editedCourse.value.features_en.push(featureInputEn.value)
    featureInputEn.value = ''
  }
}

const removeFeature = (lang, index) => {
  if (lang === 'ru') editedCourse.value.features_ru.splice(index, 1)
  else if (lang === 'kz') editedCourse.value.features_kz.splice(index, 1)
  else if (lang === 'en') editedCourse.value.features_en.splice(index, 1)
}

const saveCourse = async () => {
  savingCourse.value = true
  try {
    // Clean up temporary fields
    const payload = { ...editedCourse.value }
    delete payload.activeLangTab

    if (payload.id) {
      await api.put(`/courses/${payload.id}`, payload)
      showSuccessMessage.value = true
    } else {
      await api.post('/courses', payload)
      showSuccessMessage.value = true
    }
    await fetchCourses()
    closeCourseDialog()
  } catch (error) {
    showError(t('settings.script.errors.saveCourse'))
  } finally {
    savingCourse.value = false
  }
}

const confirmDeleteCourse = (course) => {
  courseToDelete.value = course
  deleteDialog.value = true
}

const deleteCourse = async () => {
  if (!courseToDelete.value) return
  try {
    await api.delete(`/courses/${courseToDelete.value.id}`)
    await fetchCourses()
    deleteDialog.value = false
    courseToDelete.value = null
    showSuccessMessage.value = true
  } catch (error) {
    showError(t('settings.script.errors.deleteCourse'))
  }
}

const toggleCourseActive = async (course) => {
  try {
    await api.patch(`/courses/${course.id}/toggle`)
    await fetchCourses()
  } catch (error) {
    showError(t('settings.script.errors.toggleStatus'))
  }
}
</script>

<style scoped>
.admin-settings {
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
}

.v-card {
  background: rgba(30, 30, 30, 0.8) !important;
  border: 1px solid rgba(255, 193, 7, 0.2);
}

.v-text-field,
.v-textarea {
  margin-bottom: 8px;
}

.v-tab {
  min-width: 140px;
}

.text-gradient {
  background: linear-gradient(45deg, #FFC107 30%, #FF9800 90%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.v-switch {
  margin-bottom: 16px;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .v-container {
    padding: 12px;
  }
  
  .v-tab {
    min-width: auto;
    padding: 8px 12px;
  }
  
  .v-card-text {
    padding: 16px !important;
  }
}
</style>
